import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Non-empty tuple so callers can use PRESET_COLORS[0] as a guaranteed default
// under noUncheckedIndexedAccess.
export const PRESET_COLORS: [string, ...Array<string>] = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#F43F5E', // Rose
  '#64748B', // Slate
]

interface ColorPickerProps {
  value: string | null
  onChange: (color: string | null) => void
  /** Show a "Default" swatch to clear the color */
  allowClear?: boolean
}

export function ColorPicker({ value, onChange, allowClear = false }: ColorPickerProps) {
  const { t } = useTranslation('common')
  const currentColor = value || PRESET_COLORS[0]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {allowClear && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className={cn(
              'relative size-8 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border border-border bg-background',
            )}
            aria-label={t('iconPicker.resetColor')}
          >
            {value === null && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="size-3 rounded-full bg-foreground/50" />
              </div>
            )}
            {/* Diagonal line to indicate "none" */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[1px] h-5 bg-muted-foreground/40 rotate-45" />
            </div>
          </button>
        )}
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            className="group relative size-8 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            style={{ backgroundColor: color }}
            aria-label={`Select color ${color}`}
          >
            {currentColor === color && value !== null && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="size-3 rounded-full bg-card" />
              </div>
            )}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="color"
          value={currentColor}
          onChange={(e) => onChange(e.target.value)}
          className="w-16 h-9 p-1 cursor-pointer"
        />
        <Input
          type="text"
          value={value ? value.toUpperCase() : ''}
          onChange={(e) => {
            const v = e.target.value
            if (/^#[0-9A-F]{0,6}$/i.test(v)) {
              onChange(v)
            }
          }}
          placeholder={t('iconPicker.colorPlaceholder')}
          maxLength={7}
          className="flex-1 font-mono uppercase"
        />
      </div>
    </div>
  )
}
